import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import * as XLSX from 'xlsx';
import '../../css/Pages.css';
import ScientificArticleListing from '../../components/ScientificPublications/ScientificArticle/ScientificArticleListing';
import ScientificArticleForm from '../../components/ScientificPublications/ScientificArticle/ScientificArticleForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';
import { detectDuplicates } from '../../utils/duplicateChecker';
import DuplicateWarningModal from '../../components/DuplicateWarningModal';

const ScientificArticle = () => {
    const today = new Date().toISOString().split('T')[0];
    const toast = useRef(null);
    const focusTrapRef = useRef(null);

    const initialForm = {
        Title: '', Summary: '', Keywords: '', LanguageName: '',
        JournalCategory: '',
        JournalId: null, JournalName: '', ISSN_ISBN: '', JournalType: '', JournalScore: 0, JournalStatus: '',
        ResearchFieldId: '', ArticleTypeId: '', IssueNumber: '', PageRange: '', Publisher: '', PublicationDate: today,
        IsInsideProject: false, ProjectCode: '', TotalAuthors: 1, PrimaryAuthors: 1, ContactAuthors: 1, OriginalAuthors: '',
        BonusCoefficient: '1.0', ArticleUrl: '', ScimagoUrl: '', InternationalProofUrl: '', EvidenceFileName: '', MembersJSON: '[]',
        Status: 'Lưu nháp'
    };

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [filters, setFilters] = useState({
        keyword: '', status: '', journalType: '', fromDate: null, toDate: null
    });

    const [sortConfig, setSortConfig] = useState({ key: 'Title', direction: 'asc' });

    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [members, setMembers] = useState([]);
    const [editId, setEditId] = useState(null);

    const [users, setUsers] = useState([]);
    const [researchFields, setResearchFields] = useState([]);
    const [articleTypes, setArticleTypes] = useState([]);
    const [journalTypes, setJournalTypes] = useState([]);

    const [duplicateGroups, setDuplicateGroups] = useState([]);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const currentUserId = useMemo(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                return Number(JSON.parse(userStr).Id);
            } catch (e) { return 0; }
        }
        return 0;
    }, []);

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        toast.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn hoặc không có quyền truy cập!', life: 3000 });
        setTimeout(() => {
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
            window.location.href = '/login';
        }, 1500);
    }, []);

    const fetchDropdowns = useCallback(async () => {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        try {
            const headers = getAuthHeaders();
            const [uRes, rfRes, atRes, jtRes] = await Promise.all([
                fetch(`${baseUrl}/user?mode=selection`, { headers }),
                fetch(`${baseUrl}/researchfield`, { headers }),
                fetch(`${baseUrl}/articletype`, { headers }),
                fetch(`${baseUrl}/journaltype`, { headers })
            ]);

            if (uRes.status === 401 || rfRes.status === 401 || atRes.status === 401 || jtRes.status === 401 ||
                uRes.status === 403 || rfRes.status === 403 || atRes.status === 403 || jtRes.status === 403) {
                handleLogout();
                return;
            }

            if (uRes.ok) setUsers(await uRes.json());
            if (rfRes.ok) setResearchFields(await rfRes.json());
            if (atRes.ok) setArticleTypes(await atRes.json());
            if (jtRes.ok) {
                const jtData = await jtRes.json();
                setJournalTypes(Array.isArray(jtData) ? jtData : (jtData?.data || []));
            }
        } catch (error) { console.error("Lỗi tải danh mục bài báo:", error); }
    }, [getAuthHeaders, handleLogout]);

    const fetchData = useCallback(async (isSilent = false) => {
        if (!isSilent) setIsLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            if (!currentUser || !currentUser.Id) {
                handleLogout();
                return;
            }

            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const url = `${baseUrl}/scientificarticle?userId=${currentUser.Id}&isAdmin=false`;

            const response = await fetch(url, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                const result = await response.json();
                setData(result);
            } else {
                throw new Error("Phản hồi từ máy chủ không hợp lệ");
            }
        } catch (error) {
            console.error("Lỗi fetch data:", error);
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi tải dữ liệu bài báo!', life: 3000 });
        } finally {
            if (!isSilent) setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchData();
        fetchDropdowns();
    }, [fetchData, fetchDropdowns]);

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);

    useEffect(() => {
        let result = [...data];

        if (filters.keyword) {
            const kw = filters.keyword.toLowerCase();
            result = result.filter(item =>
                (item.Title?.toLowerCase().includes(kw)) ||
                (item.JournalName?.toLowerCase().includes(kw))
            );
        }

        if (filters.status) {
            result = result.filter(item => item.Status === filters.status);
        }

        if (filters.journalType) {
            result = result.filter(item => item.JournalType === filters.journalType);
        }

        if (filters.fromDate) {
            const filterFrom = new Date(filters.fromDate);
            filterFrom.setHours(0, 0, 0, 0);
            result = result.filter(item => {
                if (!item.PublicationDate) return false;
                return new Date(item.PublicationDate).getTime() >= filterFrom.getTime();
            });
        }

        if (filters.toDate) {
            const filterTo = new Date(filters.toDate);
            filterTo.setHours(23, 59, 59, 999);
            result = result.filter(item => {
                if (!item.PublicationDate) return false;
                return new Date(item.PublicationDate).getTime() <= filterTo.getTime();
            });
        }

        if (sortConfig !== null) {
            result.sort((a, b) => {
                if (sortConfig.key === 'PublicationDate') {
                    const dateA = a.PublicationDate ? new Date(a.PublicationDate).getTime() : 0;
                    const dateB = b.PublicationDate ? new Date(b.PublicationDate).getTime() : 0;
                    if (dateA < dateB) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (dateA > dateB) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                } else {
                    const valA = (a[sortConfig.key] || '').toString().trim();
                    const valB = (b[sortConfig.key] || '').toString().trim();

                    const comparison = valA.localeCompare(valB, 'vi', { sensitivity: 'base' });
                    return sortConfig.direction === 'asc' ? comparison : -comparison;
                }
            });
        }

        setFilteredData(result);
    }, [data, filters, sortConfig]);

    useEffect(() => {
        if (data && data.length > 0) {
            const groups = detectDuplicates(data);
            setDuplicateGroups(groups);
        } else {
            setDuplicateGroups([]);
        }
    }, [data]);

    const handleStatusChange = async (id, newStatus) => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            const userId = currentUser ? currentUser.Id : null;
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticle?id=${id}&status=${encodeURIComponent(newStatus)}&userId=${userId}`, {
                method: 'PATCH',
                headers: getAuthHeaders()
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                toast.current.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? "Gửi yêu cầu duyệt thành công!" : "Hủy yêu cầu duyệt thành công!", life: 3000
                });
                fetchData(true);
            } else {
                const resData = await response.json();
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: resData.message || "Thao tác thất bại!", life: 3000 });
            }
        } catch (error) { toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 }); }
    };

    const handleBulkStatusChange = async (ids, newStatus) => {
        if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt để có thể thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Lưu nháp') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã gửi duyệt');
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã gửi duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            const userId = currentUser ? currentUser.Id : null;

            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticle?id=${id}&status=${encodeURIComponent(newStatus)}&userId=${userId}`, {
                    method: 'PATCH',
                    headers: getAuthHeaders()
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? `Đã gửi yêu cầu duyệt ${ids.length} mục!` : `Đã hủy gửi duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (editId) {
            const currentItem = data.find(d => d.Id === editId);
            if (currentItem?.Status === 'Đã gửi duyệt' || currentItem?.Status === 'Đã duyệt') {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Dữ liệu đã khóa, không thể lưu!', life: 3000 });
            }
        }

        const currentUser = JSON.parse(localStorage.getItem('user')) || {};
        if (!currentUser || !currentUser.Id) {
            return handleLogout();
        }

        if (members.length === 0) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng thêm ít nhất một thành viên!', life: 3000 });
        }

        const method = editId ? 'PUT' : 'POST';

        const payload = {
            ...formData,
            UserId: currentUser.Id,
            CreatedByEmail: currentUser.Email || '',
            MembersJSON: JSON.stringify(members),
            Id: editId ? editId : 0
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticle`, {
                method,
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            let result;
            const contentType = response.headers.get("content-type");

            if (contentType && contentType.includes("application/json")) {
                try {
                    result = await response.json();
                } catch (e) {
                    result = { status: 'error', message: 'Dữ liệu JSON từ máy chủ bị hỏng!' };
                }
            } else {
                const textError = await response.text();
                console.error("Lỗi Server trả về HTML/Text:", textError);

                let errorDetail = 'Lỗi máy chủ không xác định!';
                if (response.status === 413) errorDetail = 'Dung lượng tệp đính kèm quá lớn, máy chủ từ chối!';
                if (response.status === 500) errorDetail = 'Máy chủ gặp sự cố nội bộ (Internal Server Error)!';

                result = { status: 'error', message: errorDetail };
            }

            if (response.ok && result.status !== 'error') {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: editId ? 'Cập nhật thành công!' : 'Thêm mới thành công!', life: 3000 });
                fetchData(true);
                closeModal();
            } else {
                const errorMsg = result.message || 'Lưu thất bại!';
                toast.current?.show({ severity: 'error', summary: 'Không thể lưu', detail: errorMsg, life: 6000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        }
    };

    const handleEdit = async (item) => {
        let parsedMembers = [];
        try { parsedMembers = JSON.parse(item.MembersJSON || '[]'); } catch (e) { parsedMembers = []; }

        const missingIds = parsedMembers
            .map(m => m.UserId)
            .filter(id => id && !users.find(u => u.Id.toString() === id.toString()));

        if (missingIds.length > 0) {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?includeIds=${missingIds.join(',')}`, {
                    headers: getAuthHeaders()
                });
                if (res.ok) {
                    const extraUsers = await res.json();
                    setUsers(prev => {
                        const merged = [...prev];
                        extraUsers.forEach(eu => {
                            if (!merged.find(u => u.Id === eu.Id)) merged.push(eu);
                        });
                        return merged;
                    });
                }
            } catch (error) { console.error("Lỗi tải thông tin user bổ sung:", error); }
        }

        setEditId(item.Id);
        setFormData({
            ...initialForm,
            ...item,
            ResearchFieldId: item.ResearchFieldId || '',
            ArticleTypeId: item.ArticleTypeId || '',
            IsInsideProject: !!item.IsInsideProject
        });
        setMembers(parsedMembers);
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        const item = data.find(d => d.Id === id);
        if (item?.Status === 'Đã gửi duyệt' || item?.Status === 'Đã duyệt') {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không thể xóa hồ sơ đã gửi hoặc đã duyệt!', life: 3000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa', message: 'Bạn có chắc chắn muốn xóa bài báo - tham luận này?',
            accept: async () => {
                try {
                    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
                    const userId = currentUser ? currentUser.Id : null;
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticle?id=${id}&userId=${userId}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders()
                    });

                    if (res.status === 401 || res.status === 403) {
                        handleLogout();
                        return;
                    }

                    if (res.ok) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa bản ghi!' });
                        fetchData(true);
                    } else {
                        const err = await res.json();
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: err.message || 'Xóa thất bại!' });
                    }
                } catch (error) { toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' }); }
            }
        });
    };

    const handleBulkDelete = (ids) => {
        const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
        if (invalidItems.length > 0) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa hàng loạt',
            message: `Bạn có chắc chắn muốn xóa ${ids.length} bài báo - tham luận đã chọn?`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
                    const userId = currentUser ? currentUser.Id : null;

                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticle?id=${id}&userId=${userId}`, {
                            method: 'DELETE',
                            headers: getAuthHeaders()
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: `Đã xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi xóa thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const closeModal = () => { setIsModalOpen(false); setFormData(initialForm); setMembers([]); setEditId(null); };

    const exportToExcel = () => {
        const dataToExport = filteredData;
        if (dataToExport.length === 0) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        const internationalArticles = [];
        const domesticArticles = [];
        const qStats = { Q1: 0, Q2: 0, Q3: 0, Q4: 0, Khac: 0 };

        const removeAccents = (str) => {
            return str ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase() : '';
        };

        const checkIsInternational = (item) => {
            const typeStr = removeAccents(item.JournalType || '');
            const nameStr = removeAccents(item.JournalName || '');
            const catStr = removeAccents(item.JournalCategory || '');
            const titleStr = item.Title || '';

            const qRank = (item.QRanking || '').trim().toUpperCase();
            const catRaw = (item.JournalCategory || '').trim().toUpperCase();

            const hasVietnameseTitle = /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i.test(titleStr);

            const strictDomesticJournals = [
                'kinh te va phat trien', 'khoa hoc kinh te', 'kh & cn', 'khoa hoc va cong nghe',
                'dai hoc', 'ky yeu', 'quoc gia', 'dhdn', 'thuong mai', 'quan ly', 'phat trien kinh te',
                'journal of science and technology', 'science and technology development',
                'ho chi minh', 'vnu', 'kinh te va quan ly', 'tap chi vn'
            ];

            if (strictDomesticJournals.some(kw => nameStr.includes(kw))) {
                return false;
            }

            const isQ = ['Q1', 'Q2', 'Q3', 'Q4'];
            if (isQ.includes(qRank) || isQ.includes(catRaw)) return true;

            const strongIntlKeywords = ['scopus', 'scie', 'ssci', 'isi', 'esci', 'springer', 'emerald', 'elsevier', 'mdpi'];
            if (strongIntlKeywords.some(kw => nameStr.includes(kw) || typeStr.includes(kw))) {
                return true;
            }

            if (!hasVietnameseTitle) {
                return true;
            } else {
                return false;
            }
        };

        dataToExport.forEach(item => {
            if (checkIsInternational(item)) {
                internationalArticles.push(item);
            } else {
                domesticArticles.push(item);
            }

            const q = (item.QRanking || '').trim().toUpperCase();
            const catQ = (item.JournalCategory || '').trim().toUpperCase();
            const finalQ = ['Q1', 'Q2', 'Q3', 'Q4'].includes(q) ? q : (['Q1', 'Q2', 'Q3', 'Q4'].includes(catQ) ? catQ : 'Khac');

            if (finalQ !== 'Khac') {
                qStats[finalQ]++;
            } else {
                qStats.Khac++;
            }
        });

        const mapData = (articles) => articles.map((item, index) => ({
            'STT': index + 1,
            'Tên bài báo': item.Title || '',
            'Tác giả nguyên bản': item.OriginalAuthors || '',
            'Phân loại': checkIsInternational(item) ? 'Quốc tế' : 'Trong nước',
            'Danh mục tạp chí': item.JournalCategory || '',
            'Tên tạp chí': item.JournalName || '',
            'Chỉ số (ISSN/ISBN)': item.ISSN_ISBN || '',
            'Loại tạp chí': item.JournalType || '',
            'Q-Ranking': ['Q1', 'Q2', 'Q3', 'Q4'].includes((item.QRanking || '').toUpperCase())
                ? item.QRanking.toUpperCase()
                : (['Q1', 'Q2', 'Q3', 'Q4'].includes((item.JournalCategory || '').toUpperCase()) ? item.JournalCategory.toUpperCase() : ''),
            'Trạng thái bài báo': item.Status || 'Lưu nháp',
            'Ngày công bố': item.PublicationDate ? new Date(item.PublicationDate).toLocaleDateString('vi-VN') : '',
            'Người tạo': item.CreatorName || item.CreatedByEmail || '',
            'Ngày tạo trên HT': item.CreatedAt ? new Date(item.CreatedAt).toLocaleDateString('vi-VN') : ''
        }));

        const excelData = [];

        if (internationalArticles.length > 0) {
            excelData.push({ 'STT': 'I', 'Tên bài báo': `BÀI BÁO QUỐC TẾ (${internationalArticles.length})` });
            excelData.push(...mapData(internationalArticles));
        }

        if (domesticArticles.length > 0) {
            if (internationalArticles.length > 0) excelData.push({});
            excelData.push({ 'STT': 'II', 'Tên bài báo': `BÀI BÁO TRONG NƯỚC (${domesticArticles.length})` });
            excelData.push(...mapData(domesticArticles));
        }

        excelData.push({});
        excelData.push({});
        excelData.push({ 'STT': '', 'Tên bài báo': 'THỐNG KÊ TỔNG HỢP:' });
        excelData.push({ 'STT': '', 'Tên bài báo': `- Tổng số bài báo: ${dataToExport.length} (Quốc tế: ${internationalArticles.length}, Trong nước: ${domesticArticles.length})` });
        excelData.push({ 'STT': '', 'Tên bài báo': `- Thống kê theo chỉ số Q-Ranking:` });
        excelData.push({ 'STT': '', 'Tên bài báo': `   + Q1: ${qStats.Q1} bài` });
        excelData.push({ 'STT': '', 'Tên bài báo': `   + Q2: ${qStats.Q2} bài` });
        excelData.push({ 'STT': '', 'Tên bài báo': `   + Q3: ${qStats.Q3} bài` });
        excelData.push({ 'STT': '', 'Tên bài báo': `   + Q4: ${qStats.Q4} bài` });
        excelData.push({ 'STT': '', 'Tên bài báo': `   + Khác/Không phân loại: ${qStats.Khac} bài` });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "DanhSachBaiBao");

        const wscols = [
            { wch: 5 }, { wch: 45 }, { wch: 25 }, { wch: 15 }, { wch: 20 },
            { wch: 35 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 20 },
            { wch: 15 }, { wch: 25 }, { wch: 15 }
        ];
        worksheet['!cols'] = wscols;
        XLSX.writeFile(workbook, `DanhSachBaiBao_${today}.xlsx`);
    };

    const handleAddNew = () => {
        const currentUser = JSON.parse(localStorage.getItem('user')) || {};
        setMembers([{
            UserId: currentUser.Id || '',
            FullName: `${currentUser.LastName || ''} ${currentUser.FirstName || ''}`.trim(),
            IsFirstAuthor: false,
            IsContactAuthor: false,
            OrganizationCount: 1,
            IsStudent: false,
            IsInstructor: true
        }]);
        setEditId(null);
        setFormData(initialForm);
        setIsModalOpen(true);
    };

    const handleDropdownShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector('.dropdown-panel-override');
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };
                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleDropdownHide = () => {
        const panel = document.querySelector('.dropdown-panel-override');
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" />
            <div className="page-header">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>QUẢN LÝ BÀI BÁO - THAM LUẬN</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                <button className="btn-add-new" onClick={handleAddNew} style={{ margin: 0 }}>
                    <i className="fa-solid fa-plus"></i> Thêm mới
                </button>
                <button
                    className="btn-add-new"
                    style={{ backgroundColor: '#1a9953', margin: 0 }}
                    onClick={exportToExcel}
                >
                    <i className="fa-solid fa-file-excel"></i> Xuất Excel
                </button>

                {duplicateGroups.length > 0 && (
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#e74c3c', margin: 0, animation: 'pulse 2s infinite' }}
                        onClick={() => setIsDuplicateModalOpen(true)}
                    >
                        <i className="fa-solid fa-triangle-exclamation"></i> Phát hiện {duplicateGroups.length} nhóm trùng lặp
                    </button>
                )}
            </div>

            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <p style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#555' }}>DANH SÁCH BÀI BÁO - THAM LUẬN</p>

                <div style={{
                    display: isDesktop ? 'grid' : 'flex',
                    gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : 'none',
                    flexWrap: isDesktop ? 'nowrap' : 'wrap',
                    gap: '15px',
                    marginBottom: '15px',
                    alignItems: 'center'
                }}>
                    <div className="search-wrapper" style={{ position: 'relative', flex: isDesktop ? 'none' : '1 1 250px' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888', zIndex: 2 }}></i>
                        <input
                            type="text"
                            name="keyword"
                            placeholder="Tìm kiếm"
                            className="form-input"
                            style={{ paddingLeft: '35px', width: '100%' }}
                            value={filters.keyword}
                            onChange={handleFilterChange}
                        />
                    </div>

                    <select
                        name="status"
                        className="form-input"
                        style={{ flex: isDesktop ? 'none' : '1 1 200px', height: '40px' }}
                        value={filters.status}
                        onChange={handleFilterChange}
                    >
                        <option value="">Tất cả trạng thái</option>
                        <option value="Lưu nháp">Lưu nháp</option>
                        <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                        <option value="Đã duyệt">Đã duyệt</option>
                        <option value="Từ chối">Từ chối</option>
                        <option value="Yêu cầu chỉnh sửa">Yêu cầu chỉnh sửa</option>
                    </select>

                    <div style={{ flex: isDesktop ? 'none' : '1 1 200px', width: '100%' }}>
                        <Dropdown
                            value={filters.journalType}
                            options={journalTypes.map(jt => ({ label: jt.Name, value: jt.Name }))}
                            onChange={(e) => setFilters(prev => ({ ...prev, journalType: e.value }))}
                            placeholder="Tất cả loại tạp chí"
                            filter
                            filterBy="label"
                            className="form-input"
                            panelClassName="dropdown-panel-override"
                            style={{ width: '100%', height: '40px', display: 'flex', alignItems: 'center' }}
                            appendTo={document.body}
                            onShow={handleDropdownShow}
                            onHide={handleDropdownHide}
                        />
                    </div>
                </div>

                <div style={{
                    display: isDesktop ? 'grid' : 'flex',
                    gridTemplateColumns: isDesktop ? 'repeat(2, 1fr)' : 'none',
                    flexWrap: isDesktop ? 'nowrap' : 'wrap',
                    gap: '15px'
                }}>
                    <div style={{ flex: isDesktop ? 'none' : '1 1 200px', width: '100%' }}>
                        <Calendar
                            id="fromDate"
                            value={filters.fromDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.value }))}
                            placeholder="Từ ngày (Công bố)"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div style={{ flex: isDesktop ? 'none' : '1 1 200px', width: '100%' }}>
                        <Calendar
                            id="toDate"
                            value={filters.toDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.value }))}
                            placeholder="Đến ngày (Công bố)"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>
                </div>

                {(filters.keyword || filters.status || filters.journalType || filters.fromDate || filters.toDate) && (
                    <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            className="btn-add-new"
                            style={{ backgroundColor: '#95a5a6', margin: 0 }}
                            onClick={() => setFilters({ keyword: '', status: '', journalType: '', fromDate: null, toDate: null })}
                        >
                            <i className="fa-solid fa-eraser"></i> Xóa bộ lọc
                        </button>
                    </div>
                )}
            </div>

            <ScientificArticleListing
                data={filteredData}
                searchQuery={filters.keyword}
                currentUserId={currentUserId}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSendSubmit={(id) => handleStatusChange(id, 'Đã gửi duyệt')}
                onCancelSubmit={(id) => handleStatusChange(id, 'Lưu nháp')}
                onBulkSend={(ids) => handleBulkStatusChange(ids, 'Đã gửi duyệt')}
                onBulkCancel={(ids) => handleBulkStatusChange(ids, 'Lưu nháp')}
                onBulkDelete={handleBulkDelete}
                isLoading={isLoading}
                toast={toast}
                sortConfig={sortConfig}
                requestSort={(key) => {
                    let direction = 'asc';
                    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
                    setSortConfig({ key, direction });
                }}
            />

            <ScientificArticleForm
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                isEditing={!!editId}
                users={users}
                fields={researchFields}
                articleTypes={articleTypes}
                members={members}
                setMembers={setMembers}
                toast={toast}
                journalTypes={journalTypes}
            />

            <DuplicateWarningModal
                isOpen={isDuplicateModalOpen}
                onClose={() => setIsDuplicateModalOpen(false)}
                duplicateGroups={duplicateGroups}
                type="article"
                onDelete={handleDelete}
                currentUserId={currentUserId}
            />
        </div>
    );
};

export default ScientificArticle;